// src/components/orders/InvoiceActions.js — Facture : PDF + partage du fichier sur WhatsApp
// Maxula
import React, { useState } from 'react';
import { FiFileText, FiShare2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { invoiceAPI } from '../../services/api';

// 2 boutons : "Facture" (ouvre/télécharge le PDF) et "WhatsApp" (partage le PDF lui-même).
// Sur mobile (WhatsApp installé) -> partage natif du fichier PDF.
// Sur desktop -> télécharge le PDF puis ouvre WhatsApp (à joindre manuellement).
export default function InvoiceActions({ orderNumber, size = 'md' }) {
  const [loading, setLoading] = useState(false);

  const fetchMeta = async () => {
    const { data } = await invoiceAPI.orderInvoice(orderNumber);
    return data?.data || {};
  };

  const openPdf = async () => {
    // Ouvre l'onglet TOUT DE SUITE (dans le geste utilisateur) sinon le navigateur
    // bloque le popup quand window.open est appelé après l'await.
    const win = window.open('', '_blank');
    setLoading(true);
    try {
      const d = await fetchMeta();
      if (d.pdfUrl) {
        if (win) win.location.href = d.pdfUrl;
        else window.open(d.pdfUrl, '_blank');
      } else {
        if (win) win.close();
        toast.error('PDF indisponible');
      }
    } catch (e) {
      if (win) win.close();
      toast.error(e?.response?.data?.message || 'Reçu indisponible');
    } finally { setLoading(false); }
  };

  // Facture légale (nomme le vendeur, mandataire) — disponible après paiement.
  const openLegal = async () => {
    const win = window.open('', '_blank');
    setLoading(true);
    try {
      const d = await fetchMeta();
      if (d.legalPdfUrl) {
        if (win) win.location.href = d.legalPdfUrl;
        else window.open(d.legalPdfUrl, '_blank');
      } else {
        if (win) win.close();
        toast.info('La facture légale sera disponible après le paiement de la commande.');
      }
    } catch (e) {
      if (win) win.close();
      toast.error(e?.response?.data?.message || 'Facture légale indisponible');
    } finally { setLoading(false); }
  };

  const shareWhatsApp = async () => {
    setLoading(true);
    try {
      const d = await fetchMeta();
      const num = d?.invoice?.invoiceNumber || `FAC-${orderNumber}`;
      if (!d.pdfUrl) { toast.error('PDF indisponible'); return; }

      // Récupère le PDF en fichier (via l'API authentifiée)
      let blob = null;
      try {
        const r = await invoiceAPI.orderInvoicePdfBlob(orderNumber);
        blob = r?.data instanceof Blob ? r.data : new Blob([r.data], { type: 'application/pdf' });
      } catch (e) { blob = null; }

      const file = blob ? new File([blob], `${num}.pdf`, { type: 'application/pdf' }) : null;

      // 1) Partage natif du FICHIER (mobile -> propose WhatsApp directement)
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: num, text: `Facture ${num} — Maxula` });
          return;
        } catch (e) {
          if (e && e.name === 'AbortError') return; // l'utilisateur a annulé
        }
      }

      // 2) Repli desktop : télécharge le PDF + ouvre WhatsApp (à joindre)
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${num}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        toast.info('PDF téléchargé — joignez-le dans WhatsApp');
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(`Facture ${num} — Maxula`)}`, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Facture indisponible');
    } finally { setLoading(false); }
  };

  const pad = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button type="button" disabled={loading} onClick={openPdf}
        className={`flex items-center gap-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 ${pad}`}>
        <FiFileText size={14} /> Reçu
      </button>
      <button type="button" disabled={loading} onClick={openLegal}
        className={`flex items-center gap-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 ${pad}`}>
        <FiFileText size={14} /> Facture légale
      </button>
      <button type="button" disabled={loading} onClick={shareWhatsApp}
        className={`flex items-center gap-1.5 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50 ${pad}`}>
        <FiShare2 size={14} /> WhatsApp
      </button>
    </div>
  );
}
